import React, { useEffect, useState } from 'react';
import propTypes from 'prop-types';

import { navBtnPropTypes } from '../Detail/DetailPropTypes';
import {
  assembleFloorPlanCoreNavBtnsData,
  assembleFloorPlanHeaderTextData,
  showPricingTrigger,
} from '../../utils/DrupalEntityTypes/FloorPlan/FloorPlanHelpers';

import DetailHeader from '../Detail/DetailHeader';

/**
 * PibResidenceHeader - Internal header component for PIB residence detail.
 */
const PibResidenceHeader = ({
  unitData,
  title,
  isDataLoaded,
  navBtns,
  navClass,
  hasFees,
  pricingTriggered,
  platformShowPricing,
  platformShowPricingInitially,
  setPricingTriggered,
}) => {
  const [detailHeaderNavBtns, setDetailHeaderNavBtns] = useState(null);
  const [detailHeaderText, setDetailHeaderText] = useState(null);

  // Assemble header data when unit data is loaded
  useEffect(() => {
    if (!unitData || !isDataLoaded) {
      setDetailHeaderNavBtns(null);
      setDetailHeaderText(null);
      return;
    }

    // Get existing nav passed in or create new nav from unitData
    const rawNavBtns = navBtns?.length > 0
      ? [...navBtns]
      : assembleFloorPlanCoreNavBtnsData(unitData);

    const rawShowPricingTrigger = showPricingTrigger(
      pricingTriggered,
      platformShowPricing,
      platformShowPricingInitially,
      hasFees,
    );

    // Add map nav only when the map section will actually render
    // (PibResidenceDetail gates it on `unitData && svgId`).
    if (unitData.svgId) {
      rawNavBtns.push({
        section: 'map-scroll',
        text: 'Map',
      });
    }

    setDetailHeaderNavBtns(rawNavBtns);

    // My Residences is per-unit; the sold out tag is for floor-plan-level
    // inventory only — never show it here.
    const dataForHeader = { ...unitData, remainingInventory: null, soldOut: null };

    const rawDetailHeaderText = assembleFloorPlanHeaderTextData(
      dataForHeader,
      null,
      null,
      true, // hasInternalHeader is always true for PIB
      rawShowPricingTrigger,
      setPricingTriggered,
      [], // No header components for PIB (personalization not available)
    );

    setDetailHeaderText(rawDetailHeaderText);
  }, [
    unitData,
    isDataLoaded,
    navBtns,
    hasFees,
    pricingTriggered,
    platformShowPricing,
    platformShowPricingInitially,
    setPricingTriggered,
  ]);

  return (
    <DetailHeader
      title={unitData === null ? null : title}
      data={detailHeaderText}
      navBtns={detailHeaderNavBtns}
      navClassName={navClass}
    />
  );
};

PibResidenceHeader.propTypes = {
  unitData: propTypes.shape({ svgId: propTypes.string }),
  title: propTypes.string,
  isDataLoaded: propTypes.bool,
  navBtns: navBtnPropTypes,
  navClass: propTypes.string,
  hasFees: propTypes.bool,
  pricingTriggered: propTypes.bool,
  platformShowPricing: propTypes.bool,
  platformShowPricingInitially: propTypes.bool,
  setPricingTriggered: propTypes.func,
};

PibResidenceHeader.defaultProps = {
  unitData: null,
  title: null,
  isDataLoaded: false,
  navBtns: null,
  navClass: null,
  hasFees: false,
  pricingTriggered: false,
  platformShowPricing: false,
  platformShowPricingInitially: false,
  setPricingTriggered: null,
};

export default PibResidenceHeader;
